// src/components/Onboarding.jsx
// 5-step onboarding modal — Section 14 of the Nuvport OS Product Handbook
// Profile → Add Stocks → Goals → Alerts → Portfolio Value → Complete

import { useState, useRef, useEffect } from "react";
import { trackPortfolioValueSet, trackOnboardingCompleted } from "../lib/analytics";
import { searchTicker } from "../hooks/useStockData";
import { advanceOnboardingStep } from "../lib/onboarding/onboardingService";
import { useOnboardingState } from "../hooks/useOnboardingState";
import PortfolioValueStep from "./onboarding/PortfolioValueStep";
import CSVImportStep from "./onboarding/CSVImportStep";

const STEPS = [
  { id: "profile",         label: "Profile",    title: "Let's set up your profile",         subtitle: "This helps personalise your Nuvport OS experience." },
  { id: "stocks",          label: "Portfolio",  title: "Build your portfolio",               subtitle: "Add your holdings. We'll map them to live geopolitical risk zones." },
  { id: "goals",           label: "Goals",      title: "What are your investment goals?",    subtitle: "This sets your Optimizer defaults and risk tolerance starting point." },
  { id: "alerts",          label: "Alerts",     title: "Set your alert preferences",         subtitle: "We'll notify you when things that matter to your portfolio change." },
  { id: "portfolio_value", label: "Value",      title: "Set your portfolio value",           subtitle: "Required to unlock Backtest, Optimizer, Monte Carlo and P&L tracking." },
];

const PV_STEP = 4; // index of portfolio_value step

const COUNTRIES = [
  "United States", "United Kingdom", "Germany", "France", "Australia",
  "Canada", "Japan", "India", "Singapore", "Switzerland", "Other",
];


const GOAL_OPTIONS = [
  { id: "growth",       label: "GROW WEALTH",     icon: "↗", desc: "Total return focus. Optimizer targets maximum Sharpe ratio.", riskDefault: 70 },
  { id: "income",       label: "GENERATE INCOME", icon: "◈", desc: "Dividend and yield focus. Yield-weighted portfolio analysis.", riskDefault: 35 },
  { id: "preservation", label: "PRESERVE CAPITAL",icon: "◼", desc: "Low volatility priority. Conservative, drawdown-aware allocation.", riskDefault: 20 },
  { id: "trading",      label: "ACTIVE TRADING",  icon: "⇅", desc: "Momentum and technical signals. Short-term backtest windows.", riskDefault: 85 },
];

const RISK_LABELS = [
  { max: 20,  label: "Conservative",  color: "#00c8ff" },
  { max: 45,  label: "Moderate",      color: "#3ddc84" },
  { max: 70,  label: "Balanced",      color: "#ffd700" },
  { max: 100, label: "Aggressive",    color: "#ff6b35" },
];

const BENCHMARKS = [
  { id: "SPY",  label: "S&P 500",    desc: "SPY" },
  { id: "QQQ",  label: "Nasdaq 100", desc: "QQQ" },
  { id: "VTI",  label: "Total Mkt",  desc: "VTI" },
];

const ALERT_DEFAULTS = {
  earnings: true,
  georisk:  true,
  price:    true,
  macro:    true,
  weekly:   true,
};

const GREEN  = "#3ddc84";
const MUTED3 = "#888888";
const BDR_OB = "#2a2a2a";

function StepIndicator({ step, steps }) {
  return (
    <>
      <style>{`
        @keyframes ob-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(61,220,132,.4)} 50%{box-shadow:0 0 0 5px rgba(61,220,132,0)} }
        .ob-step-circle { transition:background .25s,border-color .25s; }
        .ob-step-line   { transition:background .25s; }
        /* Mobile: hide labels, shrink circles */
        @media(max-width:480px) {
          .ob-step-label  { display:none!important; }
          .ob-step-circle { width:10px!important;height:10px!important;font-size:0!important; }
          .ob-step-line   { height:2px!important; }
        }
      `}</style>

      {/* Desktop numbered circles */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 24 }}>
        {steps.map((s, i) => {
          const done    = i < step;
          const current = i === step;
          const upcoming = i > step;

          return (
            <div key={s.id} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
              {/* Connecting line — left half */}
              {i > 0 && (
                <div className="ob-step-line" style={{
                  position: "absolute",
                  left: 0, right: "50%",
                  top: 15, height: 1,
                  background: done || current ? GREEN : BDR_OB,
                }} />
              )}
              {/* Connecting line — right half */}
              {i < steps.length - 1 && (
                <div className="ob-step-line" style={{
                  position: "absolute",
                  left: "50%", right: 0,
                  top: 15, height: 1,
                  background: done ? GREEN : BDR_OB,
                }} />
              )}

              {/* Circle */}
              <div className="ob-step-circle" style={{
                width: 30, height: 30,
                borderRadius: "50%",
                border: `1px solid ${done ? GREEN : current ? GREEN : BDR_OB}`,
                background: done ? GREEN : current ? "transparent" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative", zIndex: 1, flexShrink: 0,
                animation: current ? "ob-pulse 2s ease-in-out infinite" : "none",
              }}>
                {done ? (
                  <span style={{ color: "#0a0a0a", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</span>
                ) : (
                  <span style={{
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                    fontSize: 10, fontWeight: 700,
                    color: current ? GREEN : MUTED3,
                    lineHeight: 1,
                  }}>{i + 1}</span>
                )}
              </div>

              {/* Label */}
              <span className="ob-step-label" style={{
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                fontSize: 8, letterSpacing: ".1em",
                textTransform: "uppercase",
                color: done || current ? GREEN : MUTED3,
                marginTop: 6,
                textAlign: "center",
                lineHeight: 1.2,
              }}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 38, height: 20,
        background: checked ? "var(--accent)" : "var(--surface-3)",
        borderRadius: 10,
        position: "relative",
        cursor: "pointer",
        transition: "background .2s ease",
        flexShrink: 0,
        border: `1px solid ${checked ? "var(--accent-hover)" : "var(--border)"}`,
      }}
    >
      <div style={{
        width: 14, height: 14,
        background: "#fff",
        borderRadius: "50%",
        position: "absolute",
        top: 2,
        left: checked ? 20 : 2,
        transition: "left .2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
      }} />
    </div>
  );
}

export default function Onboarding({ onComplete, addStock, userId, currentPortfolioValue = null }) {
  const [step, setStep]           = useState(0);
  const [name, setName]           = useState("");
  const [bio, setBio]             = useState("");
  const [country, setCountry]     = useState("United States");
  const [stocks, setStocks]       = useState([]);
  const [searchQ, setSearchQ]     = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [goals, setGoals]         = useState([]);
  const [riskTolerance, setRiskTolerance] = useState(50);
  const [benchmark, setBenchmark] = useState("SPY");
  const [alerts, setAlerts]       = useState({ ...ALERT_DEFAULTS });
  const [showWelcome, setShowWelcome] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [pvSubView, setPVSubView] = useState("manual"); // 'manual' | 'csv'
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);
  const startTimeRef = useRef(Date.now());

  // Resume from current_step on page refresh — useOnboardingState reads DB,
  // never localStorage. Cleanup (mounted flag) is handled inside the hook.
  const { state: savedState } = useOnboardingState(userId);
  useEffect(() => {
    if (savedState && savedState.current_step > 0 && savedState.current_step < STEPS.length) {
      setStep(savedState.current_step);
    }
  }, [savedState]);

  // Skip portfolio_value step if value already set (re-running onboarding).
  // Uses the value from settings (via prop) — no separate DB read.
  useEffect(() => {
    if (step === PV_STEP && currentPortfolioValue != null) {
      handleFinish(); // eslint-disable-line
    }
  }, [step, currentPortfolioValue]); // eslint-disable-line

  const handleSearch = (q) => {
    setSearchQ(q);
    clearTimeout(searchTimeout.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchTicker(q);
        setSearchResults((results || []).slice(0, 6).filter(r => !stocks.includes(r.symbol)));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
  };

  const addChip = (ticker) => {
    if (!stocks.includes(ticker)) setStocks(prev => [...prev, ticker]);
    setSearchQ("");
    setSearchResults([]);
    searchRef.current?.focus();
  };

  const removeChip = (ticker) => setStocks(prev => prev.filter(t => t !== ticker));

  const canProceed = () => {
    if (step === 0) return !!name.trim(); // name required
    if (step === 1) return true; // stocks optional
    if (step === 2) return goals.length > 0;
    if (step === 3) return true;
    if (step === PV_STEP) return false; // handled by PortfolioValueStep component
    return true;
  };

  // Don't write to DB here — pass the value up via onComplete so setSetting
  // is the single writer. This prevents the race condition where
  // useUserPreferences has already loaded with portfolioValue:null and
  // a direct Supabase write from this hook wouldn't update settings state.
  const handlePortfolioValueComplete = (value) => {
    trackPortfolioValueSet("manual");
    handleFinish(value);
  };

  const handleNext = async () => {
    setSaveError(false);
    if (step < STEPS.length - 1) {
      // Persist step completion to DB before advancing locally
      setSaving(true);
      try {
        await advanceOnboardingStep(userId, STEPS[step].id);
        setStep(s => s + 1);
      } catch {
        setSaveError(true);
      } finally {
        setSaving(false);
      }
    } else {
      handleFinish();
    }
  };

  const handleFinish = (portfolioValue = null) => {
    stocks.forEach(t => { try { addStock(t); } catch {} });
    trackOnboardingCompleted(Math.round((Date.now() - startTimeRef.current) / 1000));
    setShowWelcome(true);
    setTimeout(() => {
      onComplete({ displayName: name, bio, country, goals, riskTolerance, benchmark, alerts, portfolioValue });
    }, 3200);
  };

  const currentStep = STEPS[step];

  if (showWelcome) {
    const firstName = name.trim().split(" ")[0] || "there";
    return (
      <div className="onboarding-overlay" style={{ zIndex: 9999 }}>
        <style>{`
          @keyframes ob-bar-fill  { from { width: 0% } to { width: 100% } }
          @keyframes ob-fade-up   { from { opacity: 0; transform: translateY(18px) } to { opacity: 1; transform: translateY(0) } }
          @keyframes ob-blink-dot { 0%,100%{opacity:.3} 50%{opacity:1} }
          .ob-welcome-card { animation: ob-fade-up .55s ease forwards; }
          .ob-bar-fill     { animation: ob-bar-fill 3s cubic-bezier(.4,0,.2,1) forwards; }
        `}</style>

        <div className="ob-welcome-card" style={{
          background: "#0d1117",
          border: "1px solid rgba(61,220,132,.25)",
          borderRadius: 10,
          width: "min(480px, 92vw)",
          padding: "48px 40px",
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 28,
          boxShadow: "0 0 60px rgba(61,220,132,.08), 0 24px 48px rgba(0,0,0,.6)",
          textAlign: "center",
        }}>
          {/* Animated logo dot */}
          <div style={{
            width: 12, height: 12, borderRadius: "50%",
            background: GREEN, boxShadow: `0 0 16px ${GREEN}`,
            animation: "ob-blink-dot 1.2s ease-in-out infinite",
          }} />

          {/* Greeting */}
          <div>
            <h2 style={{
              fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700,
              color: "var(--text-primary)", marginBottom: 10, lineHeight: 1.2,
            }}>
              Welcome, {firstName}.
            </h2>
            <p style={{
              fontFamily: "var(--font-body)", fontSize: 14,
              color: "var(--text-secondary)", lineHeight: 1.6,
              maxWidth: 340,
            }}>
              Your terminal is being calibrated. 16 modules, live data, and 19 geopolitical risk zones — all ready for you.
            </p>
          </div>

          {/* World Monitor mini-map teaser */}
          <div style={{
            background: "rgba(61,220,132,.04)",
            border: "1px solid rgba(61,220,132,.15)",
            borderRadius: 8, padding: "16px 20px", width: "100%",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <svg viewBox="0 0 120 60" width={72} height={36} style={{ flexShrink: 0 }}>
                <rect x="0" y="0" width="120" height="60" fill="none" stroke="rgba(61,220,132,.1)" strokeWidth=".5"/>
                <path d="M18 10 Q22 8 24 14 Q26 20 23 28 Q20 36 18 42 Q15 46 13 42 Q11 36 13 28 Q14 20 18 10Z" fill="rgba(61,220,132,.1)" stroke="rgba(61,220,132,.25)" strokeWidth=".5"/>
                <path d="M52 8 Q58 6 60 12 Q62 18 60 24 Q58 20 55 22 Q52 26 50 22 Q48 16 52 8Z" fill="rgba(61,220,132,.1)" stroke="rgba(61,220,132,.25)" strokeWidth=".5"/>
                <path d="M52 28 Q56 26 58 32 Q60 40 56 50 Q52 52 49 48 Q46 42 48 34 Q49 30 52 28Z" fill="rgba(61,220,132,.1)" stroke="rgba(61,220,132,.25)" strokeWidth=".5"/>
                <path d="M65 6 Q80 4 88 10 Q94 16 92 22 Q88 20 82 22 Q76 24 70 20 Q64 16 65 6Z" fill="rgba(61,220,132,.1)" stroke="rgba(61,220,132,.25)" strokeWidth=".5"/>
                <circle cx="20" cy="26" r="2.5" fill={GREEN} style={{ animation: "ob-blink-dot 2s infinite" }}/>
                <circle cx="55" cy="14" r="2"   fill={GREEN} style={{ animation: "ob-blink-dot 2s infinite .3s" }}/>
                <circle cx="80" cy="14" r="2"   fill="#ffd700" style={{ animation: "ob-blink-dot 2s infinite .6s" }}/>
                <circle cx="68" cy="30" r="2"   fill="#ff6b35" style={{ animation: "ob-blink-dot 2s infinite .9s" }}/>
              </svg>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: GREEN, letterSpacing: ".1em", marginBottom: 4 }}>
                  WORLD MONITOR — LIVE
                </div>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                  19 geopolitical risk zones mapped to your portfolio exposure in real time.
                </div>
              </div>
            </div>
          </div>

          {/* Loading bar */}
          <div style={{ width: "100%" }}>
            <div style={{
              width: "100%", height: 3,
              background: "rgba(61,220,132,.12)",
              borderRadius: 2, overflow: "hidden",
            }}>
              <div className="ob-bar-fill" style={{
                height: "100%",
                background: `linear-gradient(90deg, rgba(61,220,132,.6), ${GREEN})`,
                borderRadius: 2,
                boxShadow: `0 0 8px rgba(61,220,132,.5)`,
                width: 0,
              }} />
            </div>
            <div style={{
              fontFamily: "var(--font-mono)", fontSize: 9, color: MUTED3,
              letterSpacing: ".1em", marginTop: 8, textAlign: "center",
            }}>
              INITIALISING TERMINAL…
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">

        {/* Header */}
        <div style={{
          padding: "20px 28px 16px",
          borderBottom: "1px solid var(--border)",
        }}>
          {/* Brand + step counter */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
            <div style={{
              width: 6, height: 6, borderRadius: "50%",
              background: GREEN, boxShadow: `0 0 8px ${GREEN}`,
              animation: "blink 1.4s ease-in-out infinite",
              flexShrink: 0,
            }} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: GREEN, letterSpacing: ".14em" }}>
              NUVPORT OS — SETUP
            </span>
            <span style={{
              marginLeft: "auto",
              fontFamily: "var(--font-mono)", fontSize: 9,
              color: MUTED3, letterSpacing: ".08em",
            }}>
              Step {step + 1} of {STEPS.length}
            </span>
          </div>

          {/* Step indicator */}
          <StepIndicator step={step} steps={STEPS} />

          {/* Step heading */}
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>
            {currentStep.title}
          </h2>
          <p style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)" }}>
            {currentStep.subtitle}
          </p>
        </div>

        {/* Step content */}
        <div style={{ padding: "24px 28px" }}>

          {/* ── Step 0: Profile ── */}
          {step === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", display: "block", marginBottom: 8 }}>
                  FULL NAME <span style={{ color: GREEN }}>*</span>
                </label>
                <input
                  className="input-dark"
                  style={{ width: "100%" }}
                  placeholder="e.g. Alex Johnson"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", display: "block", marginBottom: 8 }}>
                  COUNTRY <span style={{ color: GREEN }}>*</span>
                </label>
                <select
                  className="input-dark"
                  style={{ width: "100%", appearance: "none" }}
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                >
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", display: "block", marginBottom: 8 }}>
                  BIO / ABOUT <span style={{ color: MUTED3 }}>(OPTIONAL)</span>
                </label>
                <textarea
                  className="input-dark"
                  style={{ width: "100%", minHeight: 80, resize: "vertical", lineHeight: 1.6 }}
                  placeholder="e.g. Long-term investor focused on tech and energy sectors"
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ── Step 1: Add stocks ── */}
          {step === 1 && (
            <div>
              {/* Geo-risk aha moment banner */}
              <div style={{
                background: "rgba(61,220,132,.05)",
                border: "1px solid rgba(61,220,132,.18)",
                borderRadius: 6,
                padding: "12px 14px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}>
                {/* Mini world SVG */}
                <svg viewBox="0 0 120 60" width={80} height={40} style={{ flexShrink: 0, opacity: 0.85 }}>
                  <style>{`
                    @keyframes ob-dot-pulse { 0%,100%{opacity:.3} 50%{opacity:1} }
                  `}</style>
                  {/* Simplified continent outlines */}
                  <rect x="0" y="0" width="120" height="60" fill="none" stroke="rgba(61,220,132,.12)" strokeWidth=".5"/>
                  {/* Americas */}
                  <path d="M18 10 Q22 8 24 14 Q26 20 23 28 Q20 36 18 42 Q15 46 13 42 Q11 36 13 28 Q14 20 18 10Z" fill="rgba(61,220,132,.12)" stroke="rgba(61,220,132,.3)" strokeWidth=".5"/>
                  {/* Europe/Africa */}
                  <path d="M52 8 Q58 6 60 12 Q62 18 60 24 Q58 20 55 22 Q52 26 50 22 Q48 16 52 8Z" fill="rgba(61,220,132,.12)" stroke="rgba(61,220,132,.3)" strokeWidth=".5"/>
                  <path d="M52 28 Q56 26 58 32 Q60 40 56 50 Q52 52 49 48 Q46 42 48 34 Q49 30 52 28Z" fill="rgba(61,220,132,.12)" stroke="rgba(61,220,132,.3)" strokeWidth=".5"/>
                  {/* Asia */}
                  <path d="M65 6 Q80 4 88 10 Q94 16 92 22 Q88 20 82 22 Q76 24 70 20 Q64 16 65 6Z" fill="rgba(61,220,132,.12)" stroke="rgba(61,220,132,.3)" strokeWidth=".5"/>
                  {/* Risk zone dots */}
                  <circle cx="20" cy="26" r="2.5" fill="#3ddc84" style={{ animation: "ob-dot-pulse 2s ease-in-out infinite" }}/>
                  <circle cx="55" cy="14" r="2" fill="#3ddc84" style={{ animation: "ob-dot-pulse 2s ease-in-out infinite .4s" }}/>
                  <circle cx="80" cy="14" r="2" fill="#ffd700" style={{ animation: "ob-dot-pulse 2s ease-in-out infinite .8s" }}/>
                  <circle cx="68" cy="30" r="2" fill="#ff6b35" style={{ animation: "ob-dot-pulse 2s ease-in-out infinite 1.2s" }}/>
                  <circle cx="90" cy="38" r="1.5" fill="#3ddc84" style={{ animation: "ob-dot-pulse 2s ease-in-out infinite 1.6s" }}/>
                </svg>
                <div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: GREEN, letterSpacing: ".1em", marginBottom: 3 }}>
                    GEO-RISK INTELLIGENCE
                  </div>
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                    Your holdings will be mapped to <span style={{ color: GREEN, fontWeight: 600 }}>19 live geopolitical risk zones</span>. Add stocks to see your exposure.
                  </div>
                </div>
              </div>

              {/* Free tier counter */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 10,
              }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".1em" }}>
                  YOUR HOLDINGS
                </span>
                <span style={{
                  fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: ".08em",
                  color: stocks.length >= 5 ? "#ff6b35" : MUTED3,
                }}>
                  {stocks.length} / 5 — FREE TIER
                </span>
              </div>

              {/* Chips */}
              {stocks.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                  {stocks.map(t => (
                    <span
                      key={t}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "rgba(61,220,132,.08)",
                        border: "1px solid rgba(61,220,132,.25)",
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
                        color: GREEN,
                      }}
                    >
                      {t}
                      <button
                        onClick={() => removeChip(t)}
                        style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", padding: 0, fontSize: 11, lineHeight: 1, opacity: .7 }}
                      >✕</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search input — disabled at free limit */}
              {stocks.length < 5 ? (
                <div style={{ position: "relative" }}>
                  <input
                    ref={searchRef}
                    className="input-dark"
                    style={{ width: "100%" }}
                    placeholder="Search ticker or company name… (e.g. AAPL, Apple)"
                    value={searchQ}
                    onChange={e => handleSearch(e.target.value)}
                    autoFocus
                  />
                  {searching && (
                    <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--accent)", fontSize: 10 }}>
                      ●
                    </span>
                  )}
                </div>
              ) : (
                <div style={{
                  padding: "10px 14px",
                  background: "rgba(255,107,53,.06)",
                  border: "1px solid rgba(255,107,53,.25)",
                  borderRadius: 6,
                  fontFamily: "var(--font-body)", fontSize: 12,
                  color: "#ff6b35",
                }}>
                  Free tier limit reached. Upgrade to Pro to add unlimited holdings.
                </div>
              )}

              {/* Results dropdown */}
              {searchResults.length > 0 && (
                <div style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-strong)",
                  borderRadius: "var(--radius-md)",
                  marginTop: 4,
                  overflow: "hidden",
                }}>
                  {searchResults.map(r => (
                    <button
                      key={r.symbol}
                      onClick={() => addChip(r.symbol)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        width: "100%", padding: "9px 14px",
                        background: "transparent", border: "none",
                        cursor: "pointer", textAlign: "left",
                        borderBottom: "1px solid var(--border)",
                        transition: "background .1s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--accent-dim)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)", fontWeight: 700, width: 56, flexShrink: 0 }}>{r.symbol}</span>
                      <span style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{r.name ?? r.longname ?? r.shortname}</span>
                      {r.sector && (
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: MUTED3, letterSpacing: ".06em", flexShrink: 0, marginRight: 8 }}>{r.sector}</span>
                      )}
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--accent)", flexShrink: 0 }}>+ ADD</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Skip link */}
              <div style={{ marginTop: 14, textAlign: "right" }}>
                <button
                  onClick={handleNext}
                  style={{
                    background: "none", border: "none",
                    fontFamily: "var(--font-mono)", fontSize: 10,
                    color: MUTED3, cursor: "pointer", letterSpacing: ".08em",
                    transition: "color .15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = GREEN}
                  onMouseLeave={e => e.currentTarget.style.color = MUTED3}
                >
                  Skip for now →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Goals ── */}
          {step === 2 && (() => {
            const riskInfo = RISK_LABELS.find(r => riskTolerance <= r.max) || RISK_LABELS[RISK_LABELS.length - 1];
            const toggleGoal = (id) => {
              const opt = GOAL_OPTIONS.find(o => o.id === id);
              setGoals(prev => {
                const next = prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id];
                // Set risk default from most recently selected goal
                if (!prev.includes(id) && opt) setRiskTolerance(opt.riskDefault);
                return next;
              });
            };
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* 2×2 goal cards */}
                <div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em", marginBottom: 10 }}>
                    SELECT ALL THAT APPLY
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {GOAL_OPTIONS.map(opt => {
                      const sel = goals.includes(opt.id);
                      return (
                        <div
                          key={opt.id}
                          onClick={() => toggleGoal(opt.id)}
                          style={{
                            background: sel ? "rgba(61,220,132,.06)" : "var(--surface-2)",
                            border: `1px solid ${sel ? GREEN : "var(--border)"}`,
                            borderRadius: 6,
                            padding: "14px 14px",
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "border-color .15s, background .15s, box-shadow .15s",
                            boxShadow: sel ? "0 0 0 1px rgba(61,220,132,.12)" : "none",
                            position: "relative",
                          }}
                        >
                          {sel && (
                            <div style={{
                              position: "absolute", top: 8, right: 8,
                              width: 16, height: 16, borderRadius: "50%",
                              background: GREEN,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <span style={{ color: "#0a0a0a", fontSize: 9, fontWeight: 700, lineHeight: 1 }}>✓</span>
                            </div>
                          )}
                          <div style={{ fontSize: 16, marginBottom: 8, color: sel ? GREEN : "var(--text-secondary)" }}>
                            {opt.icon}
                          </div>
                          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: sel ? GREEN : "var(--text-primary)", letterSpacing: ".06em", marginBottom: 5 }}>
                            {opt.label}
                          </div>
                          <div style={{ fontFamily: "var(--font-body)", fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
                            {opt.desc}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Risk tolerance slider */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", letterSpacing: ".12em" }}>
                      RISK TOLERANCE
                    </span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: riskInfo.color, letterSpacing: ".06em" }}>
                      {riskInfo.label} — {riskTolerance}
                    </span>
                  </div>
                  <style>{`
                    .ob-risk-slider {
                      -webkit-appearance: none; appearance: none;
                      width: 100%; height: 4px; border-radius: 2px; outline: none; cursor: pointer;
                      background: linear-gradient(to right, ${riskInfo.color} ${riskTolerance}%, var(--border) ${riskTolerance}%);
                    }
                    .ob-risk-slider::-webkit-slider-thumb {
                      -webkit-appearance: none; appearance: none;
                      width: 16px; height: 16px; border-radius: 50%;
                      background: ${riskInfo.color};
                      border: 2px solid #0a0a0a;
                      box-shadow: 0 0 0 1px ${riskInfo.color};
                      cursor: pointer;
                    }
                    .ob-risk-slider::-moz-range-thumb {
                      width: 16px; height: 16px; border-radius: 50%;
                      background: ${riskInfo.color};
                      border: 2px solid #0a0a0a;
                      cursor: pointer;
                    }
                  `}</style>
                  <input
                    type="range"
                    className="ob-risk-slider"
                    min={0} max={100}
                    value={riskTolerance}
                    onChange={e => setRiskTolerance(Number(e.target.value))}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    {["Conservative", "Moderate", "Balanced", "Aggressive"].map(l => (
                      <span key={l} style={{ fontFamily: "var(--font-mono)", fontSize: 8, color: MUTED3, letterSpacing: ".06em" }}>{l}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Step 3: Alerts ── */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { id: "earnings", label: "EARNINGS ALERTS",  desc: "Pre-earnings flag for every holding in your portfolio" },
                { id: "georisk",  label: "GEO-RISK CHANGES", desc: "Alert when a geopolitical risk score moves significantly" },
                { id: "price",    label: "PRICE ALERTS",     desc: "Notify on large intraday or daily moves in your holdings" },
                { id: "macro",    label: "MACRO EVENTS",     desc: "Fed decisions, CPI prints, NFP, and key economic releases" },
                { id: "weekly",   label: "WEEKLY DIGEST",    desc: "Sunday summary — performance, risk shifts, and highlights" },
              ].map(alert => (
                <div
                  key={alert.id}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 14,
                    padding: "12px 14px",
                    background: alerts[alert.id] ? "rgba(61,220,132,.04)" : "var(--surface-2)",
                    border: `1px solid ${alerts[alert.id] ? "rgba(61,220,132,.2)" : "var(--border)"}`,
                    borderRadius: 6,
                    transition: "background .15s, border-color .15s",
                    cursor: "pointer",
                  }}
                  onClick={() => setAlerts(a => ({ ...a, [alert.id]: !a[alert.id] }))}
                >
                  <div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: alerts[alert.id] ? GREEN : "var(--text-primary)", marginBottom: 3 }}>
                      {alert.label}
                    </div>
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-tertiary)" }}>
                      {alert.desc}
                    </div>
                  </div>
                  <Toggle
                    checked={alerts[alert.id]}
                    onChange={v => setAlerts(a => ({ ...a, [alert.id]: v }))}
                  />
                </div>
              ))}
              <div style={{ fontFamily: "var(--font-body)", fontSize: 11, color: MUTED3, marginTop: 2, lineHeight: 1.6 }}>
                All preferences can be changed at any time in Settings → Alerts.
              </div>
            </div>
          )}

          {/* ── Step 4: Portfolio Value ── */}
          {step === PV_STEP && (
            pvSubView === "csv"
              ? <CSVImportStep
                  onComplete={handlePortfolioValueComplete}
                  onManual={() => setPVSubView("manual")}
                />
              : <PortfolioValueStep
                  onComplete={handlePortfolioValueComplete}
                  onCSVImport={() => setPVSubView("csv")}
                  userId={userId}
                />
          )}

        </div>

        {/* Footer — hidden on portfolio_value step (component has its own button) */}
        {step !== PV_STEP && <div style={{
          padding: "16px 28px",
          borderTop: "1px solid var(--border)",
          background: "var(--surface-2)",
        }}>
          {step === STEPS.length - 1 ? (
            /* Last step: full-width launch button */
            <button
              onClick={handleNext}
              disabled={saving}
              style={{
                width: "100%",
                background: saving ? "rgba(61,220,132,.5)" : GREEN,
                border: "none",
                color: "#0a0a0a",
                padding: "13px 24px",
                borderRadius: "var(--radius-md)",
                fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                letterSpacing: ".12em",
                boxShadow: saving ? "none" : `0 0 24px rgba(61,220,132,.35), 0 0 8px rgba(61,220,132,.2)`,
                transition: "box-shadow .2s, background .15s",
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.boxShadow = "0 0 36px rgba(61,220,132,.55), 0 0 12px rgba(61,220,132,.3)"; }}
              onMouseLeave={e => { if (!saving) e.currentTarget.style.boxShadow = "0 0 24px rgba(61,220,132,.35), 0 0 8px rgba(61,220,132,.2)"; }}
            >
              {saving ? "SAVING…" : "LAUNCH NUVPORT OS →"}
            </button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {step > 0 ? (
                <button
                  onClick={() => setStep(s => s - 1)}
                  disabled={saving}
                  style={{
                    background: "transparent", border: "none",
                    color: saving ? "var(--text-tertiary)" : "var(--text-tertiary)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10, cursor: saving ? "default" : "pointer", letterSpacing: ".08em",
                    opacity: saving ? 0.4 : 1,
                  }}
                >
                  ← BACK
                </button>
              ) : (
                <span />
              )}

              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <button
                  onClick={handleNext}
                  disabled={!canProceed() || saving}
                  style={{
                    background: canProceed() && !saving ? "var(--accent)" : "var(--surface-3)",
                    border: "none",
                    color: canProceed() && !saving ? "var(--bg)" : "var(--text-tertiary)",
                    padding: "9px 24px",
                    borderRadius: "var(--radius-md)",
                    fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
                    cursor: canProceed() && !saving ? "pointer" : "default",
                    letterSpacing: ".1em",
                    transition: "all .15s",
                    minWidth: 110,
                  }}
                >
                  {saving ? "SAVING…" : "CONTINUE →"}
                </button>
                {saveError && (
                  <span style={{
                    fontFamily: "var(--font-mono)", fontSize: 10,
                    color: "#ff6b35", letterSpacing: ".06em",
                  }}>
                    ⚠ Failed to save. Try again →
                  </span>
                )}
                {!saveError && step === 0 && (
                  <span style={{ fontFamily: "var(--font-body)", fontSize: 11, color: MUTED3 }}>
                    Takes less than 2 minutes to complete
                  </span>
                )}
              </div>
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}
